/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef, useMemo } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import {
  accessAPI,
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  TablaFiltrable,
  usePermiso
} from "../../shared";

export default function TarjetaProcesosVigentes({
  solicitud,
  onMarcarProcesada,
  onProcesoCreado
}) {
  // Las propuestas entran en el permiso de facturación: abrirlas pide lectura.
  // No se crean desde acá: se generan solas al cerrar la solicitud.
  const { puedeVer: puedeVerPropuestas } = usePermiso("admin.facturacion");
  const [loader, setLoader] = useState(true);
  const [procesos, setProcesos] = useState([]);
  const [propuestas, setPropuestas] = useState([]);
  const [sidebarCrear, setSidebarCrear] = useState(false);
  const [loaderCrearProceso, setLoaderCrearProceso] = useState(false);

  const toast = useRef(null);

  // Una fila por proceso, con la propuesta a la que pertenece (si tiene).
  const procesosTabla = useMemo(
    () =>
      procesos.map((proceso) => ({
        id: proceso.id,
        nombre: `${proceso.nombre} - ${proceso.nombrefantasia}`,
        certificadoTipo: proceso.certificadotipo?.nombre || "",
        cubiertoSabana: !!proceso.cubiertoSabana,
        // activo admite null (procesos viejos): solo el 0 es deshabilitado
        deshabilitado: proceso.activo === 0,
        propuesta:
          propuestas.find((pr) =>
            (pr.procesoIds || []).includes(proceso.id)
          ) || null
      })),
    [procesos, propuestas]
  );

  // Carga procesos + propuestas de la solicitud.
  function cargar() {
    accessAPI(
      "GET",
      `admin/proceso/solicitud/${solicitud.id}`,
      null,
      (respuesta) => {
        setProcesos(respuesta.proceso || []);
        setPropuestas(respuesta.propuestasComerciales || []);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar procesos",
          sticky: true
        });
        setLoader(false);
      }
    );
  }
  // Se recarga también al cerrar o reabrir la solicitud: el cierre genera la
  // propuesta comercial de los procesos que no tenían.
  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitud.id, solicitud.procesada]);

  // Estado visual de una propuesta.
  function estadoDe(propuesta) {
    if (!propuesta) return null;
    return propuesta.fechaaceptacion
      ? { texto: "Aceptada", severity: "success" }
      : propuesta.fechaemision
        ? { texto: "Emitida", severity: "warning" }
        : { texto: "Creada", severity: "danger" };
  }

  // Configuración del formulario para crear proceso
  const crearProcesoFormConfig = {
    secciones: [
      {
        nombre: "Nuevo proceso",
        campos: [
          {
            nombre: "nombrefantasia",
            tipoitemformularioid: 1,
            etiqueta: "Nombre del proceso",
            respuesta: solicitud.nombrefantasia,
            requerido: true
          },
          {
            nombre: "certificadotipoid",
            tipoitemformularioid: 5,
            etiqueta: "Tipo de certificado",
            requerido: true,
            opciones: "admin/config/certificadotipo?disponibleproceso=true",
            sinOpcionInicial: true,
            respuesta: solicitud.certificadotipo?.disponibleproceso
              ? solicitud.certificadotipo.id
              : null
          },
          {
            nombre: "esquemaid",
            tipoitemformularioid: 5,
            etiqueta: "Esquema de certificación",
            requerido: true,
            opciones: "admin/config/esquema",
            sinOpcionInicial: true,
            queryparams: ["certificadotipoid"]
          },
          {
            nombre: "normaid",
            tipoitemformularioid: 5,
            etiqueta: "Norma",
            requerido: true,
            opciones: "admin/norma",
            sinOpcionInicial: true,
            queryparams: ["certificadotipoid"]
          },
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Crear proceso",
        callback: crearProcesoSubmit,
        validar: true
      }
    ]
  };

  function crearProcesoSubmit(camposFormulario) {
    setLoaderCrearProceso(true);
    accessAPI(
      "POST",
      "admin/proceso/desdesolicitud",
      {
        ...camposFormulario,
        solicitudid: solicitud.id
      },
      (respuesta) => {
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Proceso creado correctamente"
        });
        cargar();
        // La evaluación de la solicitud depende de los procesos
        onProcesoCreado?.();
        setLoaderCrearProceso(false);
        setSidebarCrear(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear proceso",
          sticky: true
        });
        setLoaderCrearProceso(false);
      }
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Card>
        <Titulo
          botones={
            !solicitud.procesada && (
              <Boton icono negro callback={() => setSidebarCrear(true)}>
                <i className="pi pi-plus" />
              </Boton>
            )
          }
        >
          Procesos
        </Titulo>

        {loader && (
          <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} width="100%" height="2.5rem" />
            ))}
          </div>
        )}

        {!loader && procesos.length === 0 && (
          <p css={{ color: "var(--grisTextos)" }}>
            No hay procesos creados para esta solicitud.
          </p>
        )}

        {!loader && procesos.length > 0 && (
          <>
            <TablaFiltrable conFiltros={false}
              value={procesosTabla}
              size="small"
              dataKey="id"
              nombreArchivo="procesos_solicitud"
            >
              <Column
                header="Proceso"
                body={(rowData) => (
                  <div
                    css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
                  >
                    <Boton texto uri={`/proceso/${rowData.id}`}>
                      {rowData.nombre}
                    </Boton>
                    {/* No entra a la propuesta que se crea al cerrar */}
                    {rowData.deshabilitado && (
                      <Tag value="Deshabilitado" severity="secondary" />
                    )}
                  </div>
                )}
                exportValue={(rowData) =>
                  rowData.deshabilitado
                    ? `${rowData.nombre} (deshabilitado)`
                    : rowData.nombre
                }
              />
              <Column header="Tipo de certificado" field="certificadoTipo" />
              <Column
                header="Propuesta"
                style={{ width: "180px" }}
                body={(rowData) => {
                  if (rowData.cubiertoSabana) {
                    return <Tag value="Sábana" severity="info" />;
                  }
                  const estado = estadoDe(rowData.propuesta);
                  if (!estado) {
                    return <span css={{ color: "var(--grisTextos)" }}>—</span>;
                  }
                  return (
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem"
                      }}
                    >
                      {puedeVerPropuestas && (
                        <Boton
                          texto
                          uri={`/propuesta/${rowData.propuesta.id}?volver=/procesos/solicitudes/${solicitud.id}`}
                        >
                          Ver
                        </Boton>
                      )}
                      <Tag value={estado.texto} severity={estado.severity} />
                    </div>
                  );
                }}
              />
            </TablaFiltrable>
          </>
        )}
      </Card>

      <Sidebar
        visible={sidebarCrear}
        onHide={() => setSidebarCrear(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Formulario config={crearProcesoFormConfig} loader={loaderCrearProceso} />
      </Sidebar>
    </>
  );
}
